// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape } from 'react-intl';
import Loading from '../../../../elements/Loading/Loading';
import classNames from 'classnames/bind';
import { getAddressesIntlIds } from '../../../../utils/address';
import { FormValidator } from '../../../../utils/form';
import { orderVerifier } from '../../../../utils/string';
import Modal from '../../../../elements/Modal/Modal';
import environment from '../../../../environment';
import ReceptionOrder from './ReceptionOrder/ReceptionOrder';

import style from './Reception.scss';
import buttonStyle from '../../../../assets/styles/button.scss';
import inputStyle from '../../../../assets/styles/input.scss';

import type { HTTPError } from '../../../../error';
import type { Match } from '../../../../router';
import type { FormValidationResult } from '../../../../utils/form';
import type {
  Address,
  OrderItem,
  Shipment,
  ShipmentItem,
  ShipmentOrder,
  ShippingIdentity,
  User,
} from '../../../../entities';
import { fetchReceptionOrders } from '../duck';

type Props = {
  +match: Match;
  +fetchReceptionOrders: (query?: { [key: string]: string }) => void;
  +isFetchingReceptionOrders?: boolean;
  +receptionOrders: ShipmentOrder[];
  +fetchReceptionOrdersError: HTTPError;
  +isReceivingOrderItem?: boolean;
  +receiveOrderItem: (orderId: string, orderItemId: number) => void;
  +isReceivingOrderItemMap: { [key: string]: boolean };
};

type State = {
  search: {
    value: string;
    errors?: string[];
  };
  purchaser?: User;
  shippingIdentity?: ShippingIdentity;
  purchaserInfoModalVisible: boolean;
  shippingAddress?: Address;
  shipments?: Shipment[];
  orderId?: string;
  receptionCountMap: { [key: string]: string };
};

export default class Reception extends Component<Props, State> {
  formValidator: FormValidator;
  state: State = {
    search: {
      value: '',
    },
    purchaserInfoModalVisible: false,
    receptionCountMap: {},
  };

  constructor(props: Props) {
    super(props);
    this.formValidator = new FormValidator({
      search: {
        orderId: (value) => !orderVerifier(value),
      },
    });
  }

  componentDidMount(): * {
    const { receptionOrders, fetchReceptionOrders } = this.props;
    if (!receptionOrders) fetchReceptionOrders();
  }

  componentDidUpdate(prevProps: Props) {
    const { receptionOrders } = this.props;
    if (!prevProps.receptionOrders && receptionOrders) {
      let receptionCountMap = {};
      receptionOrders.forEach((shipmentOrder: ShipmentOrder) =>
        shipmentOrder.items.forEach((orderItem: OrderItem) => {
          receptionCountMap[orderItem.orderItemId] = orderItem.receivedCount;
        }));
      this.setState({ receptionCountMap });
    }
  }

  handleReceiveProduct = (orderId: string, orderItemId: number) => {
    const { receiveOrderItem } = this.props;
    receiveOrderItem(orderId, orderItemId);
  };

  handleSubmit = (e: SyntheticInputEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { search } = this.state;
    const { fetchReceptionOrders } = this.props;
    if (search.value === "") return fetchReceptionOrders();
    const errors = this.formValidator.validateForm({
      search: search.value,
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) =>
        this.setState((prevState: State) => ({
          [error.field]: {
            ...prevState[error.field],
            errors: error.errors,
          },
        })));
    } else if (search.value) {
      fetchReceptionOrders({ orderId: search.value });
    }
  }

  handleChange = (e: SyntheticInputEvent<HTMLInputElement>) => {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  };

  handleOpenModal = (purchaser: User, shippingIdentity: ShippingIdentity, shippingAddress: Address, shipments: Shipment[], orderId: string) => {
    this.setState({
      purchaserInfoModalVisible: true,
      purchaser,
      shippingIdentity,
      shippingAddress,
      shipments,
      orderId,
    });
  };

  handleCloseModal = () => {
    this.setState({
      purchaserInfoModalVisible: false,
    });
  };

  render() {
    const { isFetchingReceptionOrders, receptionOrders, isReceivingOrderItemMap } = this.props;
    const {
      search,
      purchaserInfoModalVisible,
      purchaser,
      shippingIdentity,
      shippingAddress,
      shipments,
      orderId,
      receptionCountMap,
    } = this.state;
    const searchError = !!search.errors && FormValidator.buildErrorLabel(search.errors, {
      orderVerifier: 'courier.reception.search.error.orderId',
    });
    const addressIntl = shippingAddress && getAddressesIntlIds(shippingAddress.country.code);
    return (
      <Fragment>
        <div className={style.reception}>
          <div className={style.searchBar}>
            <form className={style.form}
                  onSubmit={this.handleSubmit}>
              <FormattedMessage id="courier.reception.search.label">{
                txt => <label className={style.inputLabel}>{txt}</label>
              }</FormattedMessage>
              <FormattedMessage id="courier.reception.search.placeholder">{
                txt => <input type="text"
                              name="search"
                              value={search.value}
                              placeholder={txt}
                              autoComplete="off"
                              className={classNames(inputStyle.input, style.input,
                                { [inputStyle.red]: !!search.errors && search.errors.length })}
                              onChange={this.handleChange}/>
              }</FormattedMessage>
              <FormattedMessage id="courier.reception.search.button.label">{
                txt => <button type="submit"
                               className={classNames(buttonStyle.btn,
                                 buttonStyle.lightBlue, style.button)}>
                  {txt}</button>
              }</FormattedMessage>
              {searchError}
            </form>
          </div>
          <div className={style.ordersWrap}>
            {isFetchingReceptionOrders && <Loading type="circle"
                                                   color="lightBlue"
                                                   className={style.loading}/>}
            {!!receptionOrders && !isFetchingReceptionOrders &&
            receptionOrders.map((receptionOrder: ShipmentOrder, index: number) =>
              <ReceptionOrder
                key={`order-${index}`}
                receptionOrder={receptionOrder}
                isReceivingOrderItemMap={isReceivingOrderItemMap}
                handleOpenModal={this.handleOpenModal}
                handleReceiveProduct={this.handleReceiveProduct}
                receptionCountMap={receptionCountMap}
              />)}
          </div>
        </div>
        <Modal open={purchaserInfoModalVisible}
               handleClose={this.handleCloseModal}
               width={488}>
          <FormattedMessage id="courier.reception.modal.title">{
            txt => <h2 className={style.modalTitle}>{txt}</h2>
          }</FormattedMessage>
          {!!purchaser &&
          <div>
            <div className={style.propertiesWrap}>
              <FormattedMessage id="courier.reception.modal.name">{
                txt => <p>{txt}:</p>
              }</FormattedMessage>
              <span>{purchaser.firstName} {purchaser.lastName}</span>
              {!!shippingIdentity &&
              <Fragment>
                <FormattedMessage id="courier.reception.modal.dni">{
                  txt => <p>{txt}:</p>
                }</FormattedMessage>
                <span>{shippingIdentity.idNumber1}</span>
                <FormattedMessage id="courier.reception.modal.ruc">{
                  txt => <p>{txt}:</p>
                }</FormattedMessage>
                <span>{shippingIdentity.idNumber2}</span>
              </Fragment>}
              <FormattedMessage id="courier.reception.modal.phone">{
                txt => <p>{txt}:</p>
              }</FormattedMessage>
              <span>{purchaser.phone || '---'}</span>
              <FormattedMessage id="courier.reception.modal.email">{
                txt => <p>{txt}:</p>
              }</FormattedMessage>
              <span>{purchaser.email || '---'}</span>
            </div>
            {addressIntl && shippingAddress &&
            <div className={style.propertiesWrap}>
              <FormattedMessage id={addressIntl.address1.label}>{
                txt => <p>{txt}:</p>
              }</FormattedMessage>
              <span>{shippingAddress.address1}</span>
              {shippingAddress.address2 &&
              <Fragment>
                <FormattedMessage id={addressIntl.address2.label}>{
                  txt => <p>{txt}:</p>
                }</FormattedMessage>
                <span>{shippingAddress.address2}</span>
              </Fragment>}
              <FormattedMessage id={addressIntl.admin1.label}>{
                txt => <p>{txt}:</p>
              }</FormattedMessage>
              <span>{shippingAddress.admin1.name}</span>
              <FormattedMessage id={addressIntl.admin2.label}>{
                txt => <p>{txt}:</p>
              }</FormattedMessage>
              <span>{shippingAddress.admin2.name}</span>
              {addressIntl.admin3 && shippingAddress.admin3 &&
              <Fragment>
                <FormattedMessage id={addressIntl.admin3.label}>{
                  txt => <p>{txt}:</p>
                }</FormattedMessage>
                <span>{shippingAddress.admin3.name}</span>
              </Fragment>}
            </div>}
          </div>}
          {!!shipments && !!orderId &&
          <div className={style.shipmentsIds}>
            {shipments.map((shipment: Shipment, index: number) =>
              (shipment.courier.requiresIdentity &&
                <Fragment>
                  <div className={style.propertiesWrap}>
                    <FormattedMessage id="courier.reception.modal.ids.label">{
                      txt => <p>{txt}:</p>
                    }</FormattedMessage>
                  </div>
                  <div key={index}
                       className={style.idsWrap}>
                    <FormattedMessage id="courier.reception.modal.front">{
                      txt =>
                        <a className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}
                           href={`${environment.apiUrl}/courier/${orderId}/identity/front-image`}
                           target="_blank"
                           rel="noopener noreferrer"
                        >
                          {txt}
                        </a>
                    }</FormattedMessage>
                    <FormattedMessage id="courier.reception.modal.back">{
                      txt =>
                        <a className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}
                           href={`${environment.apiUrl}/courier/${orderId}/identity/back-image`}
                           target="_blank"
                           rel="noopener noreferrer"
                        >
                          {txt}
                        </a>
                    }</FormattedMessage>
                  </div>
                </Fragment>))}
          </div>}
        </Modal>
      </Fragment>)
  };
}

Reception.propTypes = {
  match: PropTypes.object.isRequired,
  fetchReceptionOrders: PropTypes.func.isRequired,
  isFetchingReceptionOrders: PropTypes.bool,
  receptionOrders: PropTypes.array,
  fetchReceptionOrdersError: PropTypes.object,
  isReceivingOrderItem: PropTypes.bool,
  receiveOrderItem: PropTypes.func.isRequired,
  isReceivingOrderItemMap: PropTypes.object.isRequired,
};

Reception.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/courier/recepetion/components/Reception.jsx